import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Loader2, Plus, Pencil, Trash2, ArrowLeft, Sparkles, Save, ExternalLink,
  Building2, Calendar, PoundSterling, ShieldX, Download, Upload, Search, Globe, Link
} from "lucide-react";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL Import Dialog Component
function ImportFromURLDialog({ open, onClose, onImport, importing, title }) {
  const [url, setUrl] = useState("");
  
  const handleImport = () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    onImport(url.trim());
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Import {title} from Website
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Paste the grant/funding opportunity page URL and we'll automatically extract funding amount, deadline, eligibility, focus areas, and application details.
          </p>
          <div className="space-y-2">
            <Label>Website URL</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://ukri.org/opportunity/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importing || !url.trim()}>
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Extract Data
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FUNDING_TYPES = ["grant", "programme", "partnership", "accelerator"];
const STATUSES = ["open", "opening_soon", "closed"];
const STAGES = ["pre-seed", "seed", "preclinical", "clinical"];
const GEOGRAPHIES = ["UK", "EU", "Global", "USA", "Associated Countries"];
const THERAPEUTIC_AREAS = ["Oncology", "Rare Diseases", "Neurology", "Cardiovascular", "Infectious Disease", "Autoimmune", "Respiratory", "Mental Health", "Metabolic", "Immunology"];
const MODALITIES = ["Small Molecule", "Antibody", "Cell Therapy", "Gene Therapy", "mRNA", "Vaccine", "AI Drug Discovery", "Platform Technology", "Diagnostic", "Device", "Digital Health"];

// Multi-select component
function MultiSelect({ label, options, selected, onChange }) {
  const toggleOption = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 min-h-[60px]">
        {options.map(opt => (
          <Badge
            key={opt}
            variant={selected.includes(opt) ? "default" : "outline"}
            className={`cursor-pointer transition-all ${selected.includes(opt) ? "bg-teal-600" : "hover:bg-slate-100"}`}
            onClick={() => toggleOption(opt)}
          >
            {opt}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Opportunity form dialog
function OpportunityFormDialog({ open, onClose, opportunity, onSave, saving, onImportFromURL, importing, importUrl, setImportUrl }) {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    funding_type: "grant",
    opening_date: "",
    deadline: "",
    funding_min: "",
    funding_max: "",
    geography_eligibility: [],
    stage_eligibility: [],
    therapeutic_areas: [],
    modality_tags: [],
    company_types: [],
    description: "",
    application_link: "",
    status: "open"
  });

  useEffect(() => {
    if (opportunity) {
      setForm({
        ...opportunity,
        opening_date: opportunity.opening_date?.split("T")[0] || "",
        deadline: opportunity.deadline?.split("T")[0] || "",
        funding_min: opportunity.funding_min || "",
        funding_max: opportunity.funding_max || ""
      });
    } else {
      setForm({
        name: "",
        organisation: "",
        funding_type: "grant",
        opening_date: "",
        deadline: "",
        funding_min: "",
        funding_max: "",
        geography_eligibility: [],
        stage_eligibility: [],
        therapeutic_areas: [],
        modality_tags: [],
        company_types: [],
        description: "",
        application_link: "",
        status: "open"
      });
    }
  }, [opportunity, open]);

  const handleSubmit = () => {
    if (!form.name || !form.organisation) {
      toast.error("Name and organisation are required");
      return;
    }
    
    const payload = {
      ...form,
      opening_date: form.opening_date ? new Date(form.opening_date).toISOString() : null,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      funding_min: form.funding_min ? parseInt(form.funding_min) : null,
      funding_max: form.funding_max ? parseInt(form.funding_max) : null
    };
    
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{opportunity ? "Edit Opportunity" : "Add New Opportunity"}</DialogTitle>
          <DialogDescription>
            {opportunity ? "Update the funding opportunity details" : "Create a new funding opportunity"}
          </DialogDescription>
        </DialogHeader>

        {/* URL Import Section - shown for both new and editing */}
        {onImportFromURL && (
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">
                {opportunity ? "Complete from Website" : "Import from Website"}
              </span>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://ukri.org/opportunity/..."
                  value={importUrl || ""}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && onImportFromURL(importUrl)}
                />
              </div>
              <Button 
                onClick={() => onImportFromURL(importUrl)} 
                disabled={importing || !importUrl?.trim()}
                variant="outline"
                className="text-purple-600 border-purple-300 hover:bg-purple-100"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                <span className="ml-2">{importing ? "Extracting..." : "Extract"}</span>
              </Button>
            </div>
            <p className="text-xs text-purple-700 mt-2">
              {opportunity 
                ? "Fill empty fields and add new eligibility criteria (won't overwrite existing data)"
                : "Paste a grant/funding page URL to auto-fill amount, deadline, eligibility, and more"
              }
            </p>
          </div>
        )}

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Innovate UK Biomedical Catalyst" />
            </div>
            <div className="space-y-2">
              <Label>Organisation *</Label>
              <Input value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} placeholder="e.g. Innovate UK" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.funding_type} onValueChange={(v) => setForm({ ...form, funding_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUNDING_TYPES.map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Application Link</Label>
              <Input value={form.application_link} onChange={(e) => setForm({ ...form, application_link: e.target.value })} placeholder="https://..." />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Opening Date</Label>
              <Input type="date" value={form.opening_date} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Deadline</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
          </div>

          {/* Funding Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min Funding (£)</Label>
              <Input type="number" value={form.funding_min} onChange={(e) => setForm({ ...form, funding_min: e.target.value })} placeholder="e.g. 100000" />
            </div>
            <div className="space-y-2">
              <Label>Max Funding (£)</Label>
              <Input type="number" value={form.funding_max} onChange={(e) => setForm({ ...form, funding_max: e.target.value })} placeholder="e.g. 2000000" />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Brief description of the funding opportunity..." />
          </div>

          {/* Multi-selects */}
          <MultiSelect
            label="Geography Eligibility"
            options={GEOGRAPHIES}
            selected={form.geography_eligibility}
            onChange={(v) => setForm({ ...form, geography_eligibility: v })}
          />

          <MultiSelect
            label="Stage Eligibility"
            options={STAGES}
            selected={form.stage_eligibility}
            onChange={(v) => setForm({ ...form, stage_eligibility: v })}
          />

          <MultiSelect
            label="Therapeutic Areas"
            options={THERAPEUTIC_AREAS}
            selected={form.therapeutic_areas}
            onChange={(v) => setForm({ ...form, therapeutic_areas: v })}
          />

          <MultiSelect
            label="Modality Tags"
            options={MODALITIES}
            selected={form.modality_tags}
            onChange={(v) => setForm({ ...form, modality_tags: v })}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Save className="w-4 h-4 mr-2" />
            {opportunity ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminFundingOpportunities() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [urlImportDialog, setUrlImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) {
        navigate("/auth");
        return;
      }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          fetchOpportunities();
        } else {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    } catch (error) {
      navigate("/auth");
    }
  };

  const fetchOpportunities = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/funding-opportunities`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (error) {
      console.error("Failed to fetch opportunities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const url = editingOpp 
        ? `${BACKEND_URL}/api/admin/funding-opportunities/${editingOpp.opportunity_id}`
        : `${BACKEND_URL}/api/admin/funding-opportunities`;
      
      const response = await fetch(url, {
        method: editingOpp ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (editingOpp) {
          setOpportunities(opportunities.map(o => o.opportunity_id === editingOpp.opportunity_id ? data.opportunity : o));
          toast.success("Opportunity updated");
        } else {
          setOpportunities([...opportunities, data.opportunity]);
          toast.success("Opportunity created");
        }
        setShowForm(false);
        setEditingOpp(null);
      } else {
        toast.error("Failed to save opportunity");
      }
    } catch {
      toast.error("Failed to save opportunity");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (oppId) => {
    if (!confirm("Are you sure you want to delete this opportunity?")) return;
    
    setDeleting(oppId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/funding-opportunities/${oppId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setOpportunities(opportunities.filter(o => o.opportunity_id !== oppId));
        toast.success("Opportunity deleted");
      } else {
        toast.error("Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (opp) => {
    setEditingOpp(opp);
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingOpp(null);
    setShowForm(true);
  };

  const handleImportFromURL = async (url) => {
    if (!url || !url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setImporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/funding-opportunities/import-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted_data;
        
        // Check if we're editing an existing opportunity (merge mode) or creating new (replace mode)
        const isEditing = editingOpp && editingOpp.opportunity_id;
        
        if (isEditing) {
          // MERGE MODE: Fill empty fields, append to arrays
          const mergeArrays = (existing, newItems) => {
            const existingSet = new Set((existing || []).map(i => i.toLowerCase?.() || i));
            const uniqueNew = (newItems || []).filter(i => !existingSet.has(i.toLowerCase?.() || i));
            return [...(existing || []), ...uniqueNew];
          };
          
          setEditingOpp(prev => ({
            ...prev,
            name: prev.name || extracted.name || "",
            organisation: prev.organisation || extracted.organisation || "",
            funding_type: prev.funding_type || extracted.funding_type || "grant",
            deadline: prev.deadline || extracted.deadline || "",
            funding_min: prev.funding_min || (extracted.amount_min ? parseInt(extracted.amount_min.replace(/[^0-9]/g, "")) : ""),
            funding_max: prev.funding_max || (extracted.amount_max ? parseInt(extracted.amount_max.replace(/[^0-9]/g, "")) : ""),
            geography_eligibility: mergeArrays(prev.geography_eligibility, extracted.eligibility),
            stage_eligibility: mergeArrays(prev.stage_eligibility, extracted.stages_supported),
            therapeutic_areas: mergeArrays(prev.therapeutic_areas, extracted.indications),
            modality_tags: mergeArrays(prev.modality_tags, extracted.modalities),
            description: prev.description || extracted.description || "",
            application_link: prev.application_link || extracted.url || url
          }));
          
          toast.success("Data merged! Empty fields filled and new eligibility items added.");
        } else {
          // NEW MODE: Replace all fields
          const prefilledOpp = {
            name: extracted.name || "",
            organisation: extracted.organisation || "",
            funding_type: extracted.funding_type || "grant",
            opening_date: "",
            deadline: extracted.deadline || "",
            funding_min: extracted.amount_min ? parseInt(extracted.amount_min.replace(/[^0-9]/g, "")) : "",
            funding_max: extracted.amount_max ? parseInt(extracted.amount_max.replace(/[^0-9]/g, "")) : "",
            geography_eligibility: extracted.eligibility || [],
            stage_eligibility: extracted.stages_supported || [],
            therapeutic_areas: extracted.indications || [],
            modality_tags: extracted.modalities || [],
            company_types: [],
            description: extracted.description || "",
            application_link: extracted.url || url,
            status: "open"
          };
          
          setEditingOpp(prefilledOpp);
          toast.success("Data extracted! Review the form below.");
        }
        
        setImportUrl("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to extract data");
      }
    } catch (error) {
      toast.error("Failed to extract data from URL");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/funding-opportunities`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const headers = ["name", "organisation", "funding_type", "status", "deadline", "funding_min", "funding_max", "geography_eligibility", "stage_eligibility", "therapeutic_areas", "modality_tags", "description", "application_link"];
        const csvRows = [headers.join(",")];
        
        for (const opp of data.opportunities) {
          const row = headers.map(h => {
            let val = opp[h] || "";
            if (Array.isArray(val)) val = val.join("; ");
            if (typeof val === "string" && (val.includes(",") || val.includes('"') || val.includes("\n"))) {
              val = `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          });
          csvRows.push(row.join(","));
        }
        
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `grants_export_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Grants exported successfully");
      }
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result || "";
        const rows = text.split("\n").filter(row => row.trim());
        
        if (rows.length < 2) {
          toast.error("File must have header row and at least one data row");
          return;
        }
        
        // Parse CSV
        const parseCSVRow = (row) => {
          const result = [];
          let current = "";
          let inQuotes = false;
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"') {
              if (inQuotes && row[i + 1] === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === "," && !inQuotes) {
              result.push(current.trim());
              current = "";
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVRow(rows[0]).map(h => h.toLowerCase().replace(/\s+/g, "_"));
        const oppsToUpload = [];
        
        for (let i = 1; i < rows.length; i++) {
          const values = parseCSVRow(rows[i]);
          if (values.length < 2) continue;
          
          const opp = {};
          headers.forEach((header, idx) => {
            let value = values[idx] || "";
            // Parse semicolon-separated arrays
            if (["geography_eligibility", "stage_eligibility", "therapeutic_areas", "modality_tags"].includes(header)) {
              opp[header] = value.split(";").map(v => v.trim()).filter(v => v);
            } else {
              opp[header] = value;
            }
          });
          oppsToUpload.push(opp);
        }
        
        // Upload each opportunity
        setUploading(true);
        let created = 0, errors = 0;
        
        for (const opp of oppsToUpload) {
          try {
            const response = await fetch(`${BACKEND_URL}/api/admin/funding-opportunities`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify(opp)
            });
            if (response.ok) created++;
            else errors++;
          } catch {
            errors++;
          }
        }
        
        setUploading(false);
        fetchOpportunities();
        toast.success(`Imported ${created} grants${errors > 0 ? `, ${errors} errors` : ""}`);
        
      } catch (error) {
        toast.error("Failed to parse file");
        setUploading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // Filter opportunities by search
  const filteredOpportunities = opportunities.filter(opp => {
    if (!search) return true;
    const s = search.toLowerCase();
    return opp.name?.toLowerCase().includes(s) || 
           opp.organisation?.toLowerCase().includes(s) ||
           opp.description?.toLowerCase().includes(s);
  });

  // Loading state
  if (isAdmin === null || (isAdmin && loading)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  // Access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">You don't have permission to access the admin panel.</p>
              <Button onClick={() => navigate("/dashboard")} className="btn-secondary">Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="page-title">
              Funding Opportunities
            </h1>
            <p className="text-slate-600 mt-1">Manage grants, programmes, and funding calls</p>
          </div>
        </div>

        {/* Actions Bar */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search grants..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import CSV
                </Button>
                <Button variant="outline" onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Grant
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-900">{opportunities.length}</div>
              <div className="text-sm text-slate-500">Total Opportunities</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{opportunities.filter(o => o.status === "open").length}</div>
              <div className="text-sm text-slate-500">Open</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-600">{opportunities.filter(o => o.status === "opening_soon").length}</div>
              <div className="text-sm text-slate-500">Opening Soon</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-400">{opportunities.filter(o => o.status === "closed").length}</div>
              <div className="text-sm text-slate-500">Closed</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[250px]">Name</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Funding</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOpportunities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No funding opportunities found</p>
                        <Button onClick={openCreate} variant="link" className="text-teal-600">Add your first opportunity</Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOpportunities.map((opp) => (
                      <TableRow key={opp.opportunity_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="font-medium text-slate-900">{opp.name}</div>
                          {opp.application_link && (
                            <a href={opp.application_link} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> Link
                            </a>
                          )}
                        </TableCell>
                        <TableCell>{opp.organisation}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{opp.funding_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {opp.funding_max ? `£${(opp.funding_max / 1000000).toFixed(1)}M` : "-"}
                        </TableCell>
                        <TableCell>
                          {opp.deadline ? new Date(opp.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            opp.status === "open" ? "bg-green-100 text-green-700" :
                            opp.status === "opening_soon" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-500"
                          }>
                            {opp.status.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(opp)}>
                              <Pencil className="w-4 h-4 text-slate-400 hover:text-teal-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8" 
                              onClick={() => handleDelete(opp.opportunity_id)}
                              disabled={deleting === opp.opportunity_id}
                            >
                              {deleting === opp.opportunity_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <OpportunityFormDialog
          open={showForm}
          onClose={() => { setShowForm(false); setEditingOpp(null); setImportUrl(""); }}
          opportunity={editingOpp}
          onSave={handleSave}
          saving={saving}
          onImportFromURL={handleImportFromURL}
          importing={importing}
          importUrl={importUrl}
          setImportUrl={setImportUrl}
        />
      </main>
    </div>
  );
}
